
import styles from './AgentMessage.module.css';

import { Message } from "../../types/Agent";

import Markdown from 'react-markdown';

// import rehypeMathjax from 'rehype-mathjax';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/cjs/styles/prism';

// Avatars removed from inline layout to maximize content width.
import React from 'react';
import ToolCallCard from './ToolCallCard';
import BlueprintCard from './BlueprintCard';
import { 
  AgentBadMessageError, 
  AgentCharLimitExceededError, 
  AuthorizationError, 
  InternalServerError, 
  PayloadTooLargeError, 
  UnexpectedError 
} from '@back4app2/errors';

const customStyle: {
  [key: string]: React.CSSProperties
} = {
  ...oneDark,
  'pre[class*="language-"]': {
    ...oneDark['pre[class*="language-"]'],
    backgroundColor: '#111214',
    margin: 0
  },
  'code[class*="language-"]': {
    ...oneDark['code[class*="language-"]'],
    backgroundColor: '#111214',
    margin: 0
  },
};

// Helper function to parse tool calls from the entire message content
// function parseToolCallsFromContent(content: string) {
//   const toolCalls: Array<{name: string, parameters: Record<string, any>, result?: any, startIndex: number, endIndex: number}> = [];
  
//   // Find all tool-call blocks
//   const toolCallPattern = /```tool-call\s*\n([\s\S]*?)\n```/g;
//   let match;
  
//   while ((match = toolCallPattern.exec(content)) !== null) {
//     const [fullMatch, jsonStr] = match;
    
//     try {
//       const callData = JSON.parse(jsonStr.trim());
//       const name = callData.name || callData.tool || 'unknown';
//       const parameters = callData.parameters || callData.params || {};
      
//       // Find corresponding result after this tool call
//       const afterMatch = content.substring(match.index + fullMatch.length);
//       const resultPattern = /```tool-result\s*\n([\s\S]*?)\n```/;
//       const resultMatch = afterMatch.match(resultPattern);
      
//       let result = undefined;
//       let endIndex = match.index + fullMatch.length;
      
//       if (resultMatch) {
//         try {
//           result = JSON.parse(resultMatch[1].trim());
//         } catch {
//           result = resultMatch[1].trim();
//         }
//         // Update endIndex to include the result block
//         endIndex = match.index + fullMatch.length + resultMatch[0].length;
//       }
      
//       toolCalls.push({ 
//         name, 
//         parameters, 
//         result, 
//         startIndex: match.index, 
//         endIndex 
//       });
//     } catch (e) {
//       console.warn('Failed to parse tool call:', e);
//     }
//   }
  
//   return toolCalls;
// }

// Stop marker pattern used by agent_v2.py - markdown format: \n\n---\n*message*
const STOP_MARKER_REGEX = /\n\n---\n\*This message generation was stopped by user\.\*\s*$/;

const AgentMessage = React.memo(({ message }: { message: Message}) => {
  const bgColor = message.role === 'assistant' ? 'bg-white/5' : '';
  
  // Check if message was stopped by user (agent_v2 format)
  const { contentWithoutStopMarker, wasStoppedByUser } = React.useMemo(() => {
    if (message.role !== 'assistant') {
      return { contentWithoutStopMarker: message.content, wasStoppedByUser: false };
    }
    const match = message.content.match(STOP_MARKER_REGEX);
    if (match) {
      return {
        contentWithoutStopMarker: message.content.replace(STOP_MARKER_REGEX, ''),
        wasStoppedByUser: true
      };
    }
    return { contentWithoutStopMarker: message.content, wasStoppedByUser: false };
  }, [message.content, message.role]);
  
  // Create a stable hash for the message content to use as part of the key
  const contentHash = React.useMemo(() => {
    // Create a more stable hash based on the entire content
    let hash = 0;
    for (let i = 0; i < message.content.length; i++) {
      const char = message.content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }, [message.content]);

  // Parse tool calls and results to create a lookup map
  const toolCallResults = React.useMemo(() => {
    if (message.role !== 'assistant') return new Map();
    
    const results = new Map();
    const toolCallPattern = /```tool-call\s*\n([\s\S]*?)\n```/g;
    let match;
    
    while ((match = toolCallPattern.exec(message.content)) !== null) {
      try {
        const callData = JSON.parse(match[1].trim());
        const name = callData.name || callData.tool || 'unknown';
        const parameters = callData.parameters || callData.params || {};
        
        // Find corresponding result after this tool call
        const afterMatch = message.content.substring(match.index + match[0].length);
        const resultPattern = /```tool-result\s*\n([\s\S]*?)\n```/;
        const resultMatch = afterMatch.match(resultPattern);
        
        let result = undefined;
        if (resultMatch) {
          try {
            result = JSON.parse(resultMatch[1].trim());
          } catch {
            result = resultMatch[1].trim();
          }
        }
        
        // Create a unique key for this tool call
        const toolKey = `${name}-${JSON.stringify(parameters)}`;
        results.set(toolKey, result);
      } catch (e) {
        console.warn('Failed to parse tool call:', e);
      }
    }
    
    return results;
  }, [message.content, message.role]);

  const handledAgentErrorMessage = React.useMemo(() => {
    if (!message.error || !message.error.code) return null;
    if (message.error.code === InternalServerError.CODE) return null;
    if (message.error.code === UnexpectedError.CODE) return null;
  
    if (message.error.code === AgentCharLimitExceededError.CODE || 
      message.error.code === PayloadTooLargeError.CODE) {
      return 'Message is too long. Please shorten the message and try again.';
    } else if (message.error.code === AgentBadMessageError.CODE) {
      return 'Message is not valid. Please rewrite it and try again.';
    } else if (message.error.code === AuthorizationError.CODE) {
      return 'You are not authorized to requested this action.';
    }
    return null;
  }, [message.error]);

  return (
    <div className={`notranslate flex items-start justify-start gap-3 px-3 py-3 text-sm ${bgColor}`} translate="no">
      {/* Speaker label placed above content to identify message owner without using inline avatars */}
        {message.role === 'user' ? (
          <div className="flex-1 overflow-hidden">
            <div className="text-xs text-light-blue mb-2">You</div>
            <div className="prose !prose-invert whitespace-pre-wrap w-full break-words [word-break:break-word]">
              {message.content}
            </div>
          </div>
        ) : (
          <div className='flex flex-col w-full overflow-hidden'>
            <div className="text-xs text-cta-green mb-2">Agent</div>
            <Markdown
              className="prose !prose-invert w-full break-words [word-break:break-word] overflow-hidden"
              remarkPlugins={[remarkGfm, remarkMath ]}
              // rehypePlugins={[rehypeMathjax]}
              components={{
                pre(props) {
                  const {children, ...rest} = props;
                  const codeElement = React.Children.only(children) as React.ReactElement<{className: string, children: React.ReactNode}>;
                  const {className, children: codeChildren} = codeElement.props;
                  const match = /language-([\w-]+)/.exec(className || '');
                  const codeContent = String(codeChildren).replace(/\n$/, '');
                  
                  // Check if this is a tool-call or tool-result code block
                  if (match && (match[1] === 'tool-call' || match[1] === 'tool-result')) {
                    // For tool-call blocks, render ToolCallCard
                    if (match[1] === 'tool-call') {
                      try {
                        const callData = JSON.parse(String(codeChildren).trim());
                        const name = callData.name || callData.tool || 'unknown';
                        const parameters = callData.parameters || callData.params || {};
                        
                        // Create a stable key based on content hash and tool call data
                        const toolKey = `${contentHash}-${name}-${match.index}`;
                        
                        // Look up the result for this tool call
                        const resultKey = `${name}-${JSON.stringify(parameters)}`;
                        const result = toolCallResults.get(resultKey);
                        
                        return (
                          <ToolCallCard
                            key={toolKey}
                            name={name}
                            parameters={parameters}
                            result={result}
                          />
                        );
                      } catch (e) {
                        console.warn('Failed to parse tool call:', e);
                        return <pre {...rest}>{children}</pre>;
                      }
                    }
                    // For tool-result blocks, return null as they're handled in tool-call
                    return null;
                  }
                  
                  // Check for Blueprint JSON - don't wrap in <pre>, let code handler render the card
                  if (match && match[1] === 'json' && codeContent.includes('blueprint_title')) {
                    return <>{children}</>;
                  }
                  
                  return <pre {...rest}>{children}</pre>;
                },
                code(props) {
                  const {children, className, node, ...rest} = props
                  const match = /language-([\w-]+)/.exec(className || '');
                  const content = String(children).replace(/\n$/, '');
                  
                  // Check for Blueprint JSON (task_plan) - detect by blueprint_title key
                  if (match && match[1] === 'json' && content.includes('blueprint_title')) {
                    // Clean up potential XML tags if the model hallucinates them inside the code block
                    const jsonString = content.replace(/<\/?task_plan>/g, '').trim();
                    
                    // If still typing (streaming), show placeholder card
                    if (message.isTyping) {
                      return <BlueprintCard data={{ blueprint_title: 'Generating...' }} isTyping={true} />;
                    }
                    
                    // Try to parse the complete JSON
                    try {
                      const data = JSON.parse(jsonString);
                      if (data.blueprint_title) {
                        return <BlueprintCard data={data} isTyping={false} />;
                      }
                    } catch (e) {
                      // JSON not yet complete or invalid, show placeholder
                      return <BlueprintCard data={{ blueprint_title: 'Processing...' }} isTyping={true} />;
                    }
                  }
                  
                  return match && match[1] ? (
                    <div className='codeblock overflow-x-auto'>
                      <SyntaxHighlighter
                        children={content}
                        style={customStyle}
                        language={match[1]}
                        wrapLongLines={true}
                      />
                    </div>
                  ) : (
                    <code {...rest} className={`${className || ''} break-all`}>
                      {children}
                    </code>
                  )
                },
                table({ children }) {
                  return (
                    <table className="border-collapse border border-black px-3 py-1 dark:border-white">
                      {children}
                    </table>
                  );
                },
                th({ children }) {
                  return (
                    <th className="break-words border border-black bg-gray-500 px-3 py-1 text-white dark:border-white">
                      {children}
                    </th>
                  );
                },
                td({ children }) {
                  return (
                    <td className="break-words border border-black px-3 py-1 dark:border-white">
                      {children}
                    </td>
                  );
                },
                a({ href, className, children }) {
                  return (
                    <a className={className} href={href} target="_blank" rel="noreferrer noopener">
                      {children}
                    </a>
                  );
                }
              }}
            >
              {contentWithoutStopMarker}
            </Markdown>
            
            {/* Show stopped by user indicator */}
            {wasStoppedByUser && (
              <div className="text-[#9ca3af] text-[14px] italic mt-4">
                This message generation was stopped by user.
              </div>
            )}
            
            { handledAgentErrorMessage ? (
              <div className="mt-4 text-error-red">
                {handledAgentErrorMessage}
              </div>
            ) : message.hasFailed ? (
              <div className="mt-4 text-error-red">
                Something went wrong. If this issue persists, please contact us.
              </div>
            ) : (
              <>
                {/* Show loading while typing */}
                {message.isTyping && (
                  <div className={`${styles.typingIndicator} ${message.content.trim() ? 'mt-6 ml-0' : ''}`} aria-live="polite" aria-label="Assistant is thinking">
                    <span className={styles.dot}></span>
                    <span className={styles.dot}></span>
                    <span className={styles.dot}></span>
                  </div>
                )}
              </>
            )}
          </div>
        )}
    </div>
  )
}, (prevProps, nextProps) => {
  // Only re-render if the message content actually changes
  return prevProps.message.content === nextProps.message.content &&
         prevProps.message.role === nextProps.message.role &&
         prevProps.message.isTyping === nextProps.message.isTyping &&
         prevProps.message.hasFailed === nextProps.message.hasFailed;
});

export default AgentMessage;
